/* eslint-disable valid-jsdoc */
/**
 * Session Management Configuration
 *
 * Modify these values to adjust session timeout behavior
 */

const SESSION_CONFIG = {
  /**
   * IDLE_TIMEOUT: Time of inactivity before session expires
   * User will be logged out if no mouse/keyboard/scroll activity
   * Default: 20 minutes (GO-ITS 25.13 §2.7 maximum)
   */
  IDLE_TIMEOUT: 20 * 60 * 1000, // 20 minutes in milliseconds

  /**
   * SLIDING_WINDOW: Maximum session duration with activity
   * Even if user is active, session expires after this time for security.
   * Aligned to the server-enforced absolute cap (#628); overridden at runtime by
   * the value from GET /auth/session-config. Default: 24 hours.
   */
  SLIDING_WINDOW: 24 * 60 * 60 * 1000, // 24 hours in milliseconds

  /**
   * WARNING_TIME: How long before timeout to show warning dialog
   * Default: 5 minutes
   */
  WARNING_TIME: 5 * 60 * 1000, // 5 minutes in milliseconds

  /**
   * SERVER_KEEPALIVE_INTERVAL: Minimum gap between server keepalive pings.
   * Fired only on genuine user activity (throttled). Must be < IDLE_TIMEOUT so an
   * actively-working user keeps the server-side session warm; also bounds the skew
   * between the client idle clock and the server idle clock to this value (#606).
   * Server idle window itself stays 20 min (GO-ITS 25.13 §2.7) — unchanged.
   */
  SERVER_KEEPALIVE_INTERVAL: 2 * 60 * 1000, // 2 minutes
};

/**
 * Common preset configurations
 * Uncomment one to use, or create your own custom config
 *
 * TOKEN LIFETIME AND REFRESH CADENCE ARE NO LONGER SETTABLE HERE, and these presets used to say
 * they were. Slice 3b deleted TOKEN_REFRESH_INTERVAL and TOKEN_LIFETIME: they were client-side
 * copies of a server-side number, and the copy silently disagreed with it. The token TTL is
 * `expiresIn` in server/controllers/auth.js; the refresh fires at 80% of whatever
 * localStorage.expires_at says, derived per cycle (session-manager.js scheduleNextRefresh).
 *
 * Left in rather than deleted because an operator who copies a preset from an older checkout
 * would otherwise set two keys nothing reads and get no warning — validateConfig's check for
 * them went with them.
 */

// // STRICT SECURITY (Financial/Healthcare environments)
// const SESSION_CONFIG = {
//   IDLE_TIMEOUT: 15 * 60 * 1000,        // 15 minutes
//   SLIDING_WINDOW: 8 * 60 * 60 * 1000,  // 8 hours
//   WARNING_TIME: 2 * 60 * 1000,         // 2 minutes
// };

// // BALANCED (Recommended for most enterprise apps)
// const SESSION_CONFIG = {
//   IDLE_TIMEOUT: 30 * 60 * 1000,        // 30 minutes
//   SLIDING_WINDOW: 12 * 60 * 60 * 1000, // 12 hours
//   WARNING_TIME: 5 * 60 * 1000,         // 5 minutes
// };

// // RELAXED (Internal tools, low security requirements)
// const SESSION_CONFIG = {
//   IDLE_TIMEOUT: 60 * 60 * 1000,        // 60 minutes
//   SLIDING_WINDOW: 24 * 60 * 60 * 1000, // 24 hours
//   WARNING_TIME: 10 * 60 * 1000,        // 10 minutes
// };

/**
 * Helper function to convert milliseconds to human-readable format
 */
export const formatDuration = (ms) => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    return `${seconds} second${seconds > 1 ? 's' : ''}`;
  }
};

/**
 * Validation: Ensure configuration values make sense
 */
const validateConfig = (config) => {
  const warnings = [];

  if (config.WARNING_TIME >= config.IDLE_TIMEOUT) {
    warnings.push('WARNING: WARNING_TIME should be less than IDLE_TIMEOUT');
  }

  if (config.WARNING_TIME >= config.SLIDING_WINDOW) {
    warnings.push('WARNING: WARNING_TIME should be less than SLIDING_WINDOW');
  }

  if (config.IDLE_TIMEOUT >= config.SLIDING_WINDOW) {
    warnings.push('WARNING: IDLE_TIMEOUT should be less than SLIDING_WINDOW');
  }

  if (warnings.length > 0) {
    console.warn('Session Config Validation Warnings:');
    warnings.forEach((w) => console.warn(`  - ${w}`));
  }

  return warnings.length === 0;
};

// Validate config on load
validateConfig(SESSION_CONFIG);

/**
 * Overlay the server's effective session policy (#628) so the client's idle
 * warning and sliding-window timers match what the server actually enforces.
 * WARNING_TIME is clamped below IDLE_TIMEOUT to avoid a negative setTimeout.
 */
export const applyServerConfig = (cfg) => {
  if (!cfg) return;
  if (Number.isFinite(cfg.idleMs) && cfg.idleMs > 0) SESSION_CONFIG.IDLE_TIMEOUT = cfg.idleMs;
  if (Number.isFinite(cfg.absoluteMs) && cfg.absoluteMs > 0) SESSION_CONFIG.SLIDING_WINDOW = cfg.absoluteMs;
  if (Number.isFinite(cfg.warningMs) && cfg.warningMs > 0) SESSION_CONFIG.WARNING_TIME = cfg.warningMs;
  SESSION_CONFIG.WARNING_TIME = Math.min(SESSION_CONFIG.WARNING_TIME, Math.max(1000, SESSION_CONFIG.IDLE_TIMEOUT - 1000));
};

// Fetch the server policy exactly once per app load; keep defaults on any error.
let serverConfigPromise = null;
export const loadServerConfig = () => {
  if (serverConfigPromise) return serverConfigPromise;
  serverConfigPromise = fetch('/auth/session-config')
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => applyServerConfig(cfg))
      .catch(() => {/* keep hardcoded defaults */});
  return serverConfigPromise;
};

export default SESSION_CONFIG;
